import { useEffect } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { StyleSheet, Text } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import {
  BodyText,
  Button,
  HStack,
  Modal,
  theme,
  useResponsive,
} from '@sg/garnish';

import {
  checkIfBaseIngredient,
  type CustomizationMachineTypes,
} from '../../../machine';
import { CustomizationModalContentContainer } from '../CustomizationModalContentContainer';
import { CustomizationModalHeading } from '../CustomizationModalHeading';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationTooFewBasesModal = (props: Props) => {
  const {
    isVisible,
    activeModifications,
    size = 'md',
    onRequestClose,
    onContinuePress,
    onEndPress,
    onShow,
  } = props;

  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const count = activeModifications.filter(checkIfBaseIngredient).length;
  const accessibilityLabel = formatMessage(messages.modalLabel, { count });

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isVisible) return;

    onShow?.();
  }, [isVisible, onShow]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Modal
      visible={isVisible}
      size="medium"
      accessibilityLabel={accessibilityLabel}
      onRequestClose={onRequestClose}
    >
      <CustomizationModalContentContainer size={size}>
        <CustomizationModalHeading>
          <FormattedMessage {...messages.headingStart} />

          <CustomizationModalHeading bold>
            <Text> </Text>
            <FormattedMessage {...messages.headingCount} values={{ count }} />
            <Text> </Text>
          </CustomizationModalHeading>

          <FormattedMessage {...messages.headingEnd} />
        </CustomizationModalHeading>

        <BodyText sizeMatch={['16']}>
          <FormattedMessage {...messages.text} />
        </BodyText>

        <SafeAreaView edges={match([['bottom'], undefined])}>
          <HStack
            itemsPerRow={2}
            gap={theme.spacing['4']}
            style={styles.ctaContainer}
          >
            <Button
              size={size === 'md' ? 'large' : 'large-wide'}
              style={size === 'lg' ? styles.ctaLG : undefined}
              palette="kale"
              onPress={onContinuePress}
              accessibilityLabel={formatMessage(messages.goBack)}
            >
              <FormattedMessage {...messages.goBack} />
            </Button>

            <Button
              size={size === 'md' ? 'large' : 'large-wide'}
              style={size === 'lg' ? styles.ctaLG : undefined}
              palette="primary"
              onPress={onEndPress}
              accessibilityLabel={formatMessage(messages.continue)}
            >
              <FormattedMessage {...messages.continue} />
            </Button>
          </HStack>
        </SafeAreaView>
      </CustomizationModalContentContainer>
    </Modal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  base: {
    defaultMessage: 'base',
    description: 'Customization | Too Few Bases | Base',
  },
  modalLabel: {
    defaultMessage: 'Too few bases selected ({count} portion)',
    description: 'Customization | Too Few Bases | Modal label',
  },
  headingStart: {
    defaultMessage: 'Heads up - only',
    description: 'Customization | Too Few Bases | Heading start',
  },
  headingCount: {
    defaultMessage: '{count} base',
    description: 'Customization | Too Few Bases | Heading count',
  },
  headingEnd: {
    defaultMessage: 'portion selected',
    description: 'Customization | Too Few Bases | Heading end',
  },
  text: {
    defaultMessage:
      'Get a full portion by adding another base. You can add another portion of the same base or mix it up with a different base.',
    description: 'Customization | Too Few Bases | Text',
  },
  goBack: {
    defaultMessage: 'Go back',
    description: 'Customization | Go back',
  },
  continue: {
    defaultMessage: 'Continue',
    description: 'Customization | Continue',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ctaContainer: {
    paddingTop: theme.spacing['2'],
  },
  ctaLG: {
    minHeight: 72,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  isVisible: boolean;
  activeModifications: CustomizationMachineTypes.ModificationsList;
  size?: 'md' | 'lg';
  onRequestClose: () => void;
  onContinuePress: () => void;
  onEndPress: () => void;
  onShow?: () => void;
};
